import type { MapViewportSize } from './viewportBounds';

export type { MapViewportSize } from './viewportBounds';

export type MapCameraBounds = {
  northLat: number;
  southLat: number;
  eastLng: number;
  westLng: number;
};

export type MapCameraPadding = {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
};

export type MapCamera = {
  center: { lat: number; lng: number };
  zoom: number;
};

export type StaticMapCamera = MapCamera & {
  width: number;
  height: number;
};

const WORLD_TILE_SIZE = 256;
const MIN_ZOOM = 0;
const MAX_ZOOM = 21;
const MAX_MERCATOR_LATITUDE = 85.05112878;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function normalizeLongitude(lng: number): number {
  return ((lng + 180) % 360 + 360) % 360 - 180;
}

function longitudeToWorldX(lng: number): number {
  return (normalizeLongitude(lng) + 180) / 360;
}

function latitudeToWorldY(lat: number): number {
  const clampedLat = clamp(lat, -MAX_MERCATOR_LATITUDE, MAX_MERCATOR_LATITUDE);
  const sin = Math.sin((clampedLat * Math.PI) / 180);
  return 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI);
}

function worldXToLongitude(x: number): number {
  return normalizeLongitude(x * 360 - 180);
}

function worldYToLatitude(y: number): number {
  return (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI;
}

function isFiniteBounds(bounds: MapCameraBounds): boolean {
  return (
    Number.isFinite(bounds.northLat) &&
    Number.isFinite(bounds.southLat) &&
    Number.isFinite(bounds.eastLng) &&
    Number.isFinite(bounds.westLng) &&
    bounds.northLat >= bounds.southLat
  );
}

function getAxisZoom(availablePixels: number, worldSpan: number): number {
  if (worldSpan <= 0) return MAX_ZOOM;
  return Math.log2(availablePixels / (WORLD_TILE_SIZE * worldSpan));
}

export function fitMapCamera(
  bounds: MapCameraBounds,
  viewport: MapViewportSize,
  padding: MapCameraPadding = {},
): MapCamera | null {
  if (!isFiniteBounds(bounds) || !Number.isFinite(viewport.width) || !Number.isFinite(viewport.height)) {
    return null;
  }

  const top = padding.top ?? 0;
  const right = padding.right ?? 0;
  const bottom = padding.bottom ?? 0;
  const left = padding.left ?? 0;
  const availableWidth = viewport.width - left - right;
  const availableHeight = viewport.height - top - bottom;
  if (availableWidth <= 0 || availableHeight <= 0) return null;

  const westX = longitudeToWorldX(bounds.westLng);
  let eastX = longitudeToWorldX(bounds.eastLng);
  if (eastX < westX) eastX += 1;

  const northY = latitudeToWorldY(bounds.northLat);
  const southY = latitudeToWorldY(bounds.southLat);
  const zoom = clamp(
    Math.floor(
      Math.min(getAxisZoom(availableWidth, eastX - westX), getAxisZoom(availableHeight, southY - northY)),
    ),
    MIN_ZOOM,
    MAX_ZOOM,
  );
  const worldPixels = WORLD_TILE_SIZE * 2 ** zoom;
  const centerX = (westX + eastX) / 2 - (left - right) / (2 * worldPixels);
  const centerY = (northY + southY) / 2 - (top - bottom) / (2 * worldPixels);

  return {
    center: {
      lat: worldYToLatitude(centerY),
      lng: worldXToLongitude(centerX),
    },
    zoom,
  };
}

/**
 * Snap a center onto its camera's output-pixel grid, so bounds that differ far
 * below one pixel stop forking the signature, the Google request and the CDN entry.
 */
export function quantizeCenterToPixelGrid(
  center: { lat: number; lng: number },
  zoom: number,
  scale: number,
): { lat: number; lng: number } {
  const worldPixels = WORLD_TILE_SIZE * 2 ** zoom * scale;
  if (!Number.isFinite(worldPixels) || worldPixels <= 0) return center;
  if (!Number.isFinite(center.lat) || !Number.isFinite(center.lng)) return center;

  const x = Math.round(longitudeToWorldX(center.lng) * worldPixels) / worldPixels;
  const y = Math.round(latitudeToWorldY(center.lat) * worldPixels) / worldPixels;
  return { lat: worldYToLatitude(y), lng: worldXToLongitude(x) };
}

export function fitStaticMapCamera(
  bounds: MapCameraBounds,
  viewport: MapViewportSize,
  maxDimension: number,
): StaticMapCamera | null {
  if (!Number.isFinite(maxDimension) || maxDimension <= 0) return null;

  const camera = fitMapCamera(bounds, viewport);
  if (!camera) return null;

  const imageScale = Math.min(1, maxDimension / viewport.width, maxDimension / viewport.height);
  const width = Math.max(1, Math.round(viewport.width * imageScale));
  const height = Math.max(1, Math.round(viewport.height * imageScale));
  const effectiveScale = Math.min(width / viewport.width, height / viewport.height);

  return {
    center: camera.center,
    zoom: clamp(Math.round(camera.zoom + Math.log2(effectiveScale)), MIN_ZOOM, MAX_ZOOM),
    width,
    height,
  };
}
